import Vue from "vue";
import Vuex from "vuex";
import { v4 as uuidv4 } from "uuid";

import auth from "./modules/auth.module";
import appConfig from "./modules/appConfig.module";
import userInfo from "./modules/userInfo.module";
import userWallets from "./modules/userWallets";
import websocket from "./modules/websocket.module";
import notification from "./modules/notification.module";
import projectConfig from "./modules/projectConfig.module";
import productStats from "./modules/productStats.module";
import walletUtils from "./modules/walletUtils.module";

Vue.use(Vuex);

const mutationTypes = {
  SET_CHUNK_LOADING: "global/SET_CHUNK_LOADING",
  SET_PAGE_META: "global/SET_PAGE_META",
  SET_AUTH_MODAL: "global/SET_AUTH_MODAL",
  ADD_ALERT: "global/ADD_ALERT",
  REMOVE_ALERT: "global/REMOVE_ALERT",
};

const ALERT_DISAPEAR_TIMEOUT = 8000;

const state = {
  chunkLoading: true,
  authModal: {
    shouldShow: false,
    component: "GetStarted",
    width: "604",
    data: {
      email: "",
      ecosystem: "",
      project: "",
      wallet: "",
    },
  },
  alerts: [],
};

const actions = {
  setChunkLoading({ commit }, loading) {
    commit(mutationTypes.SET_CHUNK_LOADING, loading);
  },
  setPageMeta({ commit }, { title, description, keywords }) {
    title = "Unagii App - " + (title || "Yield Smarter");
    description =
      description ||
      "The automated DeFi yield platform with staking services. All the convenience and none of the hassles for smarter access to DeFi yields.";
    keywords =
      keywords || "Unagii / DeFi / Yield / Digital assets / Crypto / Invest";
    commit(mutationTypes.SET_PAGE_META, { title, description, keywords });
  },
  toggleMobileNav({ commit }, value) {
    commit(mutationTypes.TOGGLE_MOBILE_NAV, value);
  },
  setAuthModal({ commit }, data) {
    commit(mutationTypes.SET_AUTH_MODAL, data);
  },
  showAuthModal({ dispatch }, component) {
    dispatch("setAuthModal", {
      shouldShow: true,
      component,
      width: "604",
      data: { email: "", ecosystem: "", project: "", wallet: "" },
    });
  },
  showSelectWalletModal({ dispatch, state }, data) {
    dispatch("setAuthModal", {
      shouldShow: true,
      component: "ConnectWallet",
      width: "687",
      data: data || state.authModal.data,
    });
  },
  closeAuthModal({ dispatch }) {
    dispatch("setAuthModal", {
      shouldShow: false,
      data: {},
    });
    setTimeout(() => {
      dispatch("setAuthModal", {
        component: "",
      });
    }, 500);
  },
  addAlert({ commit }, options) {
    options.expiry =
      new Date().valueOf() + (options.timeout || ALERT_DISAPEAR_TIMEOUT);
    options.id = uuidv4();
    commit(mutationTypes.ADD_ALERT, options);
  },
  removeAlert({ commit }, id) {
    commit(mutationTypes.REMOVE_ALERT, id);
  },
};

const mutations = {
  [mutationTypes.SET_CHUNK_LOADING](state, loading) {
    state.chunkLoading = loading;
  },
  [mutationTypes.SET_PAGE_META](state, { title, description, keywords }) {
    setMetaTags({ title, description, keywords });
  },
  [mutationTypes.SET_AUTH_MODAL](state, data) {
    state.authModal = {
      ...state.authModal,
      ...data,
    };
  },
  [mutationTypes.ADD_ALERT](state, options) {
    state.alerts = [options, ...state.alerts];
  },
  [mutationTypes.REMOVE_ALERT](state, id) {
    const alerts = [...state.alerts];
    const index = alerts.findIndex(al => al.id === id);
    alerts.splice(index, 1);
    state.alerts = alerts;
  },
};

const getters = {
  chunkLoading: state => state.chunkLoading,
  authModal: state => state.authModal,
  alerts: state => state.alerts,
};

const store = new Vuex.Store({
  state,
  mutations,
  actions,
  getters,
  modules: {
    auth,
    appConfig,
    userInfo,
    userWallets,
    websocket,
    notification,
    projectConfig,
    productStats,
    walletUtils
  },
});

const setMetaTags = ({ title, description, keywords }) => {
  document.title = title;
  const titleSelectors = [
    "property='og:title'",
    "name='twitter:title'",
    "itemprop=name",
  ];
  const descSelectors = [
    "name=description",
    "property='og:description'",
    "itemprop=description",
    "name='twitter:description'",
  ];
  const keywordsSelectors = ["name=keywords"];
  titleSelectors.forEach(prop => {
    const elem = document.querySelector(`meta[${prop}]`);
    if (elem) elem.content = title;
  });
  descSelectors.forEach(prop => {
    const elem = document.querySelector(`meta[${prop}]`);
    if (elem) elem.content = description;
  });
  keywordsSelectors.forEach(prop => {
    const elem = document.querySelector(`meta[${prop}]`);
    if (elem) elem.content = keywords;
  });
};

export default store;
